import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "./Icons";

const token = () => sessionStorage.getItem("jwtToken");
const authHdr = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token()}`,
});

/**
 * Calculates current, next, and historical quarters.
 */
export const getQuarterOptions = () => {
  const now = dayjs();
  const currentYear = now.year();
  const currentMonth = now.month(); // 0 - 11
  const currentQ = Math.floor(currentMonth / 3) + 1; // 1 - 4

  const nextQ = currentQ === 4 ? 1 : currentQ + 1;
  const nextQYear = currentQ === 4 ? currentYear + 1 : currentYear;

  const quarters = [
    { key: "ALL", label: "All Quarters" },
    { key: `Q${nextQ} ${nextQYear}`, label: `Q${nextQ} ${nextQYear} (Next Quarter)`, isNext: true },
    { key: `Q${currentQ} ${currentYear}`, label: `Q${currentQ} ${currentYear} (Current Quarter)`, isCurrent: true },
  ];

  // Add future quarters
  for (let i = 1; i <= 2; i++) {
    let q = nextQ + i;
    let y = nextQYear;
    while (q > 4) { q -= 4; y += 1; }
    quarters.push({ key: `Q${q} ${y}`, label: `Q${q} ${y}` });
  }

  // Add past quarters
  for (let i = 1; i <= 3; i++) {
    let q = currentQ - i;
    let y = currentYear;
    while (q < 1) { q += 4; y -= 1; }
    quarters.push({ key: `Q${q} ${y}`, label: `Q${q} ${y}` });
  }

  const seen = new Set();
  return quarters.filter(q => {
    if (seen.has(q.key)) return false;
    seen.add(q.key);
    return true;
  });
};

export const AddTaskModal = ({
  isOpen,
  onClose,
  target = null,
  defaultQuarter = "",
  defaultWorkType = "BUSINESS_WORK",
  onSuccess,
  showToast,
}) => {
  const quarterOptions = useMemo(() => getQuarterOptions(), []);
  const quarterSelectOptions = useMemo(() => quarterOptions.filter(q => q.key !== "ALL"), [quarterOptions]);
  const defaultQ = defaultQuarter || (quarterSelectOptions.find(q => q.isCurrent)?.key || quarterSelectOptions[0]?.key || "");

  const [taskForm, setTaskForm] = useState({
    id: null,
    quarter: defaultQ,
    title: "",
    description: "",
    kpi: "",
    workType: "BUSINESS_WORK",
    status: "ASSIGNED",
    dueDate: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setErrorMsg(null);
      if (target) {
        setTaskForm({
          id: target.id,
          quarter: target.quarter || defaultQ,
          title: target.title || "",
          description: target.description || "",
          kpi: target.kpi || "",
          workType: (target.workType || target.priority || "").toUpperCase().includes("UPSKILL") ? "UPSKILLING" : "BUSINESS_WORK",
          status: target.status || "ASSIGNED",
          dueDate: target.dueDate || "",
        });
      } else {
        setTaskForm({
          id: null,
          quarter: defaultQ,
          title: "",
          description: "",
          kpi: "",
          workType: defaultWorkType || "BUSINESS_WORK",
          status: "ASSIGNED",
          dueDate: "",
        });
      }
    }
  }, [isOpen, target, defaultQ, defaultWorkType]);

  const handleSaveTask = async (e) => {
    e.preventDefault();
    const isUpskilling = (taskForm.workType || "").toUpperCase().includes("UPSKILL");

    if (!taskForm.title.trim() && !isUpskilling) {
      setErrorMsg("Please enter a target title / project.");
      return;
    }

    if (isUpskilling && !taskForm.title.trim() && !taskForm.description.trim()) {
      setErrorMsg("Please enter a description of the skills you are learning.");
      return;
    }

    setSubmitting(true);
    setErrorMsg(null);

    const isEdit = !!taskForm.id;
    const url = isEdit ? `/timesheets/appraisals/${taskForm.id}` : "/timesheets/appraisals/assign";
    const method = isEdit ? "PUT" : "POST";

    const resolvedTitle = taskForm.title.trim()
      || (taskForm.description.trim()
        ? (taskForm.description.trim().length > 60 ? taskForm.description.trim().slice(0, 57) + "..." : taskForm.description.trim())
        : "Upskilling & Learning");

    try {
      const res = await fetch(url, {
        method,
        headers: authHdr(),
        body: JSON.stringify({
          quarter: taskForm.quarter,
          title: resolvedTitle,
          description: taskForm.description.trim(),
          kpi: taskForm.kpi.trim(),
          workType: taskForm.workType || "BUSINESS_WORK",
          priority: taskForm.workType || "BUSINESS_WORK",
          status: taskForm.status,
          dueDate: taskForm.dueDate || null,
        }),
      });

      if (res.ok) {
        const saved = await res.json();
        if (onSuccess) onSuccess(saved);
        if (showToast) {
          showToast(isEdit ? "Task updated successfully!" : "Task added successfully! It is now visible to you and your employer.", "success");
        }
        onClose();
      } else {
        const errText = await res.text();
        setErrorMsg(errText || "Failed to save task.");
      }
    } catch (err) {
      console.error("Error saving task:", err);
      setErrorMsg("Network error saving task.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(15, 23, 42, 0.4)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1500,
            padding: 16,
          }}
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.15 }}
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 14,
              width: "100%",
              maxWidth: 520,
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
              overflow: "hidden",
              border: "1px solid #E2EEFF",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #E2EEFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#F8FBFF",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: "#EFF6FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#2563EB",
                  }}
                >
                  <Icon.Target size={16} color="#2563EB" />
                </div>
                <h4 style={{ fontSize: "0.98rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>
                  {target ? "Edit Task / Target" : "Add New Task / Goal"}
                </h4>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: "1.1rem" }}
              >
                ✕
              </button>
            </div>

            <form
              onSubmit={handleSaveTask}
              style={{
                padding: "20px 22px",
                display: "flex",
                flexDirection: "column",
                gap: 14,
                overflowY: "auto",
                maxHeight: "calc(90vh - 65px)",
              }}
            >
              {errorMsg && (
                <div style={{ padding: "8px 12px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, color: "#DC2626", fontSize: "0.78rem", fontWeight: 600 }}>
                  {errorMsg}
                </div>
              )}

              {/* Quarter & Work Type */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>Quarter *</label>
                  <select
                    value={taskForm.quarter}
                    onChange={e => setTaskForm({ ...taskForm, quarter: e.target.value })}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", fontWeight: 600, color: "#0F172A", outline: "none", background: "#fff" }}
                    required
                  >
                    {quarterSelectOptions.map(q => (
                      <option key={q.key} value={q.key}>{q.label}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>Work Type *</label>
                  <select
                    value={taskForm.workType}
                    onChange={e => setTaskForm({ ...taskForm, workType: e.target.value, priority: e.target.value })}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", fontWeight: 600, color: "#0F172A", outline: "none", background: "#fff" }}
                  >
                    <option value="BUSINESS_WORK">Business Work</option>
                    <option value="UPSKILLING">Upskilling</option>
                  </select>
                </div>
              </div>

              {/* Progress Status Selector */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6, background: "#F8FAFC", padding: "12px 14px", borderRadius: 10, border: "1px solid #E2E8F0" }}>
                <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Update Your Progress Status:
                </label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[
                    { key: "ASSIGNED", label: "Assigned", bg: "#EFF6FF", text: "#2563EB", border: "#2563EB" },
                    { key: "IN_PROGRESS", label: "In Progress", bg: "#FFFBEB", text: "#D97706", border: "#D97706" },
                    { key: "COMPLETED", label: "Completed", bg: "#ECFDF5", text: "#059669", border: "#059669" },
                  ].map(s => {
                    const isCurrent = (taskForm.status || "ASSIGNED").toUpperCase() === s.key;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setTaskForm(prev => ({ ...prev, status: s.key }))}
                        style={{
                          padding: "8px 18px",
                          borderRadius: 8,
                          border: isCurrent ? `2px solid ${s.border}` : "1.5px solid #CBD5E1",
                          background: isCurrent ? s.bg : "#fff",
                          color: isCurrent ? s.text : "#475569",
                          fontSize: "0.82rem",
                          fontWeight: isCurrent ? 800 : 600,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          boxShadow: isCurrent ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {isCurrent && <span style={{ fontWeight: 800 }}>✓</span>}
                        <span>{s.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Dynamic Title / Deliverable */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>
                  {(taskForm.workType || "").toUpperCase().includes("UPSKILL") ? "Project Title (Optional)" : "Target Title / Project *"}
                </label>
                <input
                  type="text"
                  placeholder={(taskForm.workType || "").toUpperCase().includes("UPSKILL")
                    ? "e.g. AWS Cloud Certification or Fullstack Project (Optional)"
                    : "e.g. Implement Payment Gateway Integration & Unit Tests"}
                  value={taskForm.title}
                  onChange={e => setTaskForm({ ...taskForm, title: e.target.value })}
                  style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", outline: "none" }}
                  required={!(taskForm.workType || "").toUpperCase().includes("UPSKILL")}
                />
              </div>

              {/* Target Due Date */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>Target Due Date</label>
                <input
                  type="date"
                  value={taskForm.dueDate}
                  onChange={e => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                  style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", outline: "none" }}
                />
              </div>

              {/* Description / Scope / Skills */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>
                  {(taskForm.workType || "").toUpperCase().includes("UPSKILL") ? "Description About What Skills You Are Learning *" : "Work Scope & Details"}
                </label>
                <textarea
                  rows={3}
                  placeholder={(taskForm.workType || "").toUpperCase().includes("UPSKILL")
                    ? "Describe the skills, technologies, concepts, courses, or certifications you are learning..."
                    : "Describe what you plan to accomplish, key tasks, and background notes..."}
                  value={taskForm.description}
                  onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
                  style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.82rem", outline: "none", resize: "vertical", fontFamily: "inherit" }}
                />
              </div>

              {/* Key Deliverables & KPIs (Only for Business Work) */}
              {!(taskForm.workType || "").toUpperCase().includes("UPSKILL") && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>
                    Key Deliverables & Success Metric (KPI)
                  </label>
                  <textarea
                    rows={2}
                    placeholder="e.g. 1. Complete code coverage > 85%&#10;2. Deploy to staging by end of month"
                    value={taskForm.kpi}
                    onChange={e => setTaskForm({ ...taskForm, kpi: e.target.value })}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.82rem", outline: "none", resize: "vertical", fontFamily: "inherit" }}
                  />
                </div>
              )}

              {/* Notice banner */}
              <div style={{ padding: "8px 12px", background: "#F8FAFC", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: "0.74rem", color: "#64748B" }}>
                {(taskForm.workType || "").toUpperCase().includes("UPSKILL")
                  ? "💡 This upskilling goal will sync with the employer portal. Once you complete it, your employer can review and provide an appraisal."
                  : "💡 This task will immediately sync with the employer portal. After you complete the task, your employer can review and provide an appraisal."}
              </div>

              {/* Sticky Actions Footer */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  marginTop: 6,
                  borderTop: "1px solid #F1F5F9",
                  paddingTop: 12,
                  paddingBottom: 2,
                  position: "sticky",
                  bottom: -20,
                  background: "#fff",
                  zIndex: 10,
                }}
              >
                <button
                  type="button"
                  onClick={onClose}
                  style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", fontSize: "0.8rem", fontWeight: 600, color: "#475569", cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: "8px 20px",
                    borderRadius: 8,
                    border: "none",
                    background: "#2563EB",
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    color: "#fff",
                    cursor: "pointer",
                    opacity: submitting ? 0.6 : 1,
                    boxShadow: "0 2px 6px rgba(37,99,235,0.25)",
                  }}
                >
                  {submitting ? "Saving..." : (target ? "Update Task" : "Add Task")}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AddTaskModal;
